import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {
  findNodeHandle,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {useBottomBarOffset} from '#/lib/hooks/useBottomBarOffset'
import {
  type Classroom,
  classroomLevelName,
  useCreatePermission,
} from '#/state/classrooms'
import {useClassroomsForDidQuery} from '#/state/queries/classrooms'
import {List, type ListRef} from '#/view/com/util/List'
import {atoms as a, platform, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {useDialogControl} from '#/components/Dialog'
import {Circle_And_Square_Stroke1_Corner0_Rounded_Filled as CircleAndSquareIcon} from '#/components/icons/CircleAndSquare'
import {Lock_Stroke2_Corner0_Rounded as LockIcon} from '#/components/icons/Lock'
import {PlusSmall_Stroke2_Corner0_Rounded as PlusIcon} from '#/components/icons/Plus'
import {Text} from '#/components/Typography'
import {IS_IOS} from '#/env'
import {ClassroomCard} from './components/ClassroomCard'
import {CreateClassroomDialog} from './components/CreateClassroomDialog'
import {JoinByCodeDialog} from './components/JoinDialogs'

const ALL = '__all__'
const NO_CLASSROOMS: Classroom[] = []

interface SectionRef {
  scrollToTop: () => void
}

/**
 * The Classrooms tab.
 *
 * The one place a school creates a classroom — between Likes and Feeds, where
 * the school's own content lives rather than in a global menu. For everybody
 * else it is the list of classrooms they are in.
 *
 * The filter is for the case the spec cares about: a school with a lot of
 * classrooms, where the level you are looking for is not the level next to it,
 * and where a private school's own SYSTEM level names have to be filterable
 * alongside the shared ones.
 */
export function ClassroomsSection({
  ref,
  did,
  isMe,
  scrollElRef,
  headerHeight,
  isFocused,
  setScrollViewTag,
}: {
  ref?: React.Ref<SectionRef>
  did: string
  isMe: boolean
  scrollElRef: ListRef
  headerHeight: number
  isFocused: boolean
  setScrollViewTag: (tag: number | null) => void
}) {
  const t = useTheme()
  const {height} = useWindowDimensions()
  const bottomBarOffset = useBottomBarOffset(100)
  const {t: l} = useLingui()

  const {data} = useClassroomsForDidQuery(did)
  const owned = data?.owned ?? NO_CLASSROOMS
  const joined = data?.member ?? NO_CLASSROOMS
  const permission = useCreatePermission()

  const [levelFilter, setLevelFilter] = useState<string>(ALL)
  const [interest, setInterest] = useState<string>(ALL)

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {},
  }))

  useEffect(() => {
    if (IS_IOS && isFocused && scrollElRef.current) {
      const nativeTag = findNodeHandle(scrollElRef.current)
      setScrollViewTag(nativeTag)
    }
  }, [isFocused, scrollElRef, setScrollViewTag])

  /*
   * Somebody else's profile shows the rooms they run. A private classroom is
   * shown only to the school that runs it and the people already in it, which
   * the server applies to the list it sends; the reader's own tab is
   * untouched, because there they are the school or the member.
   */
  const items = useMemo<Classroom[]>(
    () => (isMe ? [...owned, ...joined] : owned),
    [isMe, joined, owned],
  )
  const joinedIds = useMemo(
    () => new Set(isMe ? joined.map(c => c.id) : []),
    [isMe, joined],
  )

  /*
   * A classroom with no level contributes no chip: there is no level to name,
   * and an empty chip beside the named ones reads as one that failed to load.
   * Those rooms are still listed under *All levels*, which is where a filter
   * that does not apply to them belongs.
   *
   * The chips are the names the **cards print** (`classroomLevelName`), not the
   * record's own level: a school that named its room *ISET Sfax génie civil* has
   * a card that says that, and a chip that said `Other` beside it would be a
   * filter for a room the reader cannot see. Two rooms that print the same name
   * share a chip, wherever their records agree or not.
   */
  const levels = useMemo(
    () => unique(items.map(classroomLevelName).filter(Boolean)),
    [items],
  )
  const interests = useMemo(
    () => unique(items.flatMap(c => c.interests)),
    [items],
  )

  const filtered = useMemo(
    () =>
      items.filter(
        c =>
          (levelFilter === ALL || classroomLevelName(c) === levelFilter) &&
          (interest === ALL || c.interests.includes(interest)),
      ),
    [interest, items, levelFilter],
  )

  const createControl = useDialogControl()
  const joinControl = useDialogControl()

  /*
   * Cards, not rows.
   *
   * The list used to be one flat column with a hairline rule between entries, which
   * is the shape of a settings page: the cover bled to the screen's edges and the
   * words underneath it looked like the next line of the last card. A card carries
   * its own border (`ClassroomCard`), so the rule is gone and the space between two
   * cards is the space between two cards.
   */
  const renderItem = useCallback(
    ({item, index}: {item: Classroom; index: number}) => (
      <View style={[a.px_lg, a.pb_md, index === 0 && a.pt_sm]}>
        <ClassroomCard classroom={item} joined={joinedIds.has(item.id)} />
      </View>
    ),
    [joinedIds],
  )

  const hasAny = items.length > 0

  return (
    <View testID="classroomsSection">
      <List
        testID="classroomsList"
        ref={scrollElRef}
        data={filtered}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        headerOffset={headerHeight}
        contentContainerStyle={{
          minHeight: height + headerHeight,
          paddingBottom: bottomBarOffset,
        }}
        removeClippedSubviews
        desktopFixedHeight
        ListHeaderComponent={
          /*
           * The head of the list is a title, a count, and the two ways a room arrives.
           *
           * The count used to sit *below* the buttons, as a line of its own between
           * the actions and the filters, where it read as a third button somebody had
           * forgotten to style; it belongs under the title it counts. The two ways in
           * are one row rather than two, so the page opens on a list instead of on a
           * column of buttons — and the filters only appear when there is something to
           * filter (one level is not a choice).
           */
          <View style={[a.px_lg, a.pt_lg, a.gap_md]}>
            <View style={[a.flex_row, a.align_center, a.gap_sm]}>
              <Text style={[a.text_xl, a.font_bold]}>
                {isMe ? l`Your classrooms` : l`Classrooms`}
              </Text>
              {isMe && permission.allowed ? (
                <Button
                  testID="classroomCreateButton"
                  label={l`Create a classroom`}
                  color="primary"
                  size="small"
                  style={[a.ml_auto]}
                  onPress={() => createControl.open()}>
                  <ButtonIcon icon={PlusIcon} />
                  <ButtonText>{l`Create`}</ButtonText>
                </Button>
              ) : null}
            </View>

            {/*
            The other way a room arrives, beside the number of them.

            *Join with a code* used to have a row to itself, above a count that had
            a row to itself, which is two thin lines of nothing between the title
            and the list. A reader who runs a school can be handed a code too, so the
            two belong on one line: the way in on the left, how many rooms there are
            on the right.

            Both wait for the first room, and this row is where the code door
            lives on this page. The empty state below no longer offers one: a code
            is looked up among the classrooms this device already holds, so with an
            empty list there is nothing for it to match and the button could only
            ever refuse. A classroom arrives by invitation link, and that link is
            what the empty state points at (`EmptyClassrooms`).
          */}
            {hasAny ? (
              <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_sm]}>
                {isMe ? (
                  <Button
                    testID="classroomJoinButton"
                    label={l`Join with a code`}
                    color="secondary"
                    size="small"
                    onPress={() => joinControl.open()}>
                    <ButtonIcon icon={LockIcon} />
                    <ButtonText>{l`Join with a code`}</ButtonText>
                  </Button>
                ) : null}
                <Text
                  numberOfLines={1}
                  style={[
                    a.text_sm,
                    t.atoms.text_contrast_medium,
                    isMe ? a.ml_auto : null,
                  ]}>
                  {plural(items.length, {
                    one: '# classroom',
                    other: '# classrooms',
                  })}
                </Text>
              </View>
            ) : null}

            {hasAny ? (
              <>
                {levels.length > 1 ? (
                  <FilterRow
                    label={l`Level`}
                    allLabel={l`All levels`}
                    options={levels}
                    value={levelFilter}
                    onChange={setLevelFilter}
                    testIDPrefix="classroomLevelFilter"
                  />
                ) : null}
                {interests.length > 1 ? (
                  <FilterRow
                    label={l`Interest`}
                    allLabel={l`All interests`}
                    options={interests}
                    value={interest}
                    onChange={setInterest}
                    testIDPrefix="classroomInterestFilter"
                  />
                ) : null}
              </>
            ) : null}
          </View>
        }
        ListEmptyComponent={
          filtered.length === 0 && hasAny ? (
            <View style={[a.px_lg, a.py_4xl, a.align_center]}>
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                {l`No classroom matches these filters.`}
              </Text>
            </View>
          ) : (
            <EmptyClassrooms
              isMe={isMe}
              canCreate={isMe && permission.allowed}
              onCreate={() => createControl.open()}
            />
          )
        }
        onRefresh={() => {}}
        refreshing={false}
      />

      <CreateClassroomDialog control={createControl} />
      <JoinByCodeDialog control={joinControl} />
    </View>
  )
}

function keyExtractor(item: Classroom) {
  return item.id
}

function unique(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b))
}

/**
 * A filter, on one line: what it filters by, then the choices.
 *
 * The label used to sit above its chips, in small grey type, which cost a line per
 * filter and made two filters look like four. Sitting in front of the choices — and
 * scrolling with them out of the way — it is a caption rather than a heading.
 */
function FilterRow({
  label,
  allLabel,
  options,
  value,
  onChange,
  testIDPrefix,
}: {
  label: string
  allLabel: string
  options: string[]
  value: string
  onChange: (next: string) => void
  testIDPrefix: string
}) {
  const t = useTheme()
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[
        a.flex_row,
        a.align_center,
        a.gap_sm,
        {paddingRight: 16},
      ]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_medium]}>
        {label}
      </Text>
      <FilterChip
        testID={`${testIDPrefix}-all`}
        selected={value === ALL}
        label={allLabel}
        onPress={() => onChange(ALL)}
      />
      {options.map(option => (
        <FilterChip
          key={option}
          testID={`${testIDPrefix}-${option}`}
          selected={value === option}
          label={option}
          onPress={() => onChange(option)}
        />
      ))}
    </ScrollView>
  )
}

/**
 * One filter choice — a chip, not a button.
 *
 * The chips these replaced were full buttons: 33px tall, a shadow of the Create
 * button's weight, and taller than the cards' own chips. A filter is a small
 * decision, so it is drawn small — the same shape as the facts on the cards below,
 * filled in the app's blue only while it is the one in force.
 */
function FilterChip({
  selected,
  label,
  onPress,
  testID,
}: {
  selected: boolean
  label: string
  onPress: () => void
  testID?: string
}) {
  const t = useTheme()
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{selected}}
      onPress={onPress}
      style={[
        styles.filterChip,
        selected
          ? {backgroundColor: t.palette.primary_500}
          : t.atoms.bg_contrast_50,
        platform({web: {cursor: 'pointer'}, default: null}),
      ]}>
      <Text
        style={[
          a.text_sm,
          a.font_semi_bold,
          selected ? {color: t.palette.white} : t.atoms.text_contrast_high,
        ]}>
        {label}
      </Text>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
  },
})

function EmptyClassrooms({
  isMe,
  canCreate,
  onCreate,
}: {
  isMe: boolean
  canCreate: boolean
  onCreate: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View style={[a.px_lg, a.py_4xl, a.align_center, a.gap_md]}>
      <CircleAndSquareIcon
        size="3xl"
        fill={t.atoms.text_contrast_medium.color}
      />
      {/*
        The line follows who is reading it, and each reader is told the thing that
        is true for them. A school is told what a code does, because a school is
        the one that hands them out. A learner is told where a classroom comes
        from, which is the **invitation link** and not the code: a code is looked
        up among the classrooms this device already holds, so in an empty list
        there is nothing for it to match, and an *enter your code* button here
        would be a door that could only ever refuse. The code door is on the
        header row above, where it is reachable once a classroom has arrived — and
        a learner cannot create a room, so there is no button for them here at all
        (`canCreate` is false for them, the same rule that keeps the Create button
        off the header).
      */}
      <Text style={[a.text_md, a.text_center, t.atoms.text_contrast_high]}>
        {!isMe
          ? l`No classrooms yet.`
          : canCreate
            ? l`Create a classroom and hand out its code. Students join with it, and their parents come along.`
            : l`No classroom yet. Open the invitation link a school sent you and the classroom arrives here.`}
      </Text>
      {isMe && canCreate ? (
        <View style={[a.flex_row, a.gap_sm]}>
          <Button
            testID="classroomEmptyCreateButton"
            label={l`Create a classroom`}
            color="primary"
            size="small"
            onPress={onCreate}>
            <ButtonIcon icon={PlusIcon} />
            <ButtonText>{l`Create`}</ButtonText>
          </Button>
        </View>
      ) : null}
    </View>
  )
}
